import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { CallAPIService } from './call-api.service';
import { UtilityService } from './utility.service';
import { CachingService } from './caching.service';
import { LanguageHandlerService } from './language-handler.service';
import { ErrorDialogComponent } from '../components/utility/error-dialog/error-dialog.component';
import { UpdateAppDialogComponent } from '../components/utility/update-app-dialog/update-app-dialog.component';
import { decryptData } from '../helpers/enc-dec.helper';
import { devicePlatforms } from '../constants/device-platforms';
import { environment } from 'src/environments/environment';
import { UserProfile } from '../models/user-profile.model';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  constructor(
    private matDialog: MatDialog,
    private callApiService: CallAPIService,
    private utilityService: UtilityService,
    private cachingService: CachingService,
    private langHandlerService: LanguageHandlerService,
    @Inject(PLATFORM_ID) private platformId:string
  ) {}

  /**
   * Method that is used to decrypt user profile details on web.
   * @param encryptAuthDtls - Encrypted user profile details.
   * @returns Decrypted user profile details.
   */
  private decryptUserProfile(encryptAuthDtls: string) {
    try {
      const decryptedDtls = decryptData(encryptAuthDtls);
      return JSON.parse(decryptedDtls);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Method for formatting parsing JSON string of user-roles.
   * @param roles - JSON string for user-roles.
   * @returns Parsed array of user-roles.
   */
  private formatUserRoles(roles: string) {
    try {
      return JSON.parse(roles);
    } catch (error) {
      return [];
    }
  }

  /**
   * Method that formated user profile for web.
   * @param authDetails - Unformatted user profile fetched from `localStorage` or `user-profile.json`.
   * @returns Formatted user profile.
   */
  private formatAuthForWeb(authDetails: any) {
    return {
      usrid: authDetails?.userinfo?.uid || '',
      umang_id: authDetails?.userinfo?.umang_id || '',
      nam: authDetails?.userinfo?.nam || '',
      gndr: authDetails?.userinfo?.gndr || '',
      age: '',
      mno: authDetails?.userinfo?.mno || '',
      email: authDetails?.userinfo?.email || '',
      addr: authDetails?.userinfo?.addr || '',
      tkn: authDetails?.tkn || '',
      lang: this.langHandlerService.currentLang,
      did: authDetails?.macAddress || '',
      ver: 0,
      roles: this.formatUserRoles(authDetails?.userinfo?.roles),
    };
  }

  /**
   * Method that formats user profile for native devices.
   * @param authDetails - Unformatted user profile fetched from `cookie`.
   * @returns Formatted user profile.
   */
  private formatAuthForNative(authDetails: any) {
    return {
      usrid: authDetails?.userinfo?.uid || '',
      umang_id: authDetails?.userinfo?.umang_id || '',
      nam: authDetails?.userinfo?.nam || '',
      age: authDetails?.userinfo?.age || '',
      gndr: authDetails?.userinfo?.gndr || '',
      mno: authDetails?.mno || '',
      email: authDetails?.userinfo?.email || '',
      addr: authDetails?.userinfo?.addr || '',
      tkn: authDetails?.tkn || '',
      lang: this.langHandlerService.currentLang,
      did: authDetails?.did || '',
      clid: authDetails?.clid || '',
      ver: +authDetails?.ver || 0,
      roles: this.formatUserRoles(authDetails?.userinfo?.roles),
    };
  }

  /**
   * Method that generated `trkr` for a particular user.
   * @returns Generated `trkr`.
   */
  private generateTRKR() {
    const lowercaseChars = [
      'a',
      'b',
      'c',
      'd',
      'e',
      'f',
      'g',
      'h',
      'i',
      'j',
      'k',
      'l',
      'm',
      'n',
      'o',
      'p',
      'q',
      'r',
      's',
      't',
      'u',
      'v',
      'w',
      'x',
      'y',
      'z',
    ];

    const uppercaseChars = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
    ];

    const counter =
      lowercaseChars[Math.floor(Math.random() * 26)] +
      uppercaseChars[Math.floor(Math.random() * 26)] +
      new Date().toISOString().slice(-24).replace(/\D/g, '').slice(0, 14);

    return `UW-${counter}`;
  }

  /**
   * Method to check if current app. is valid or not.
   * @param deviceOS - OS of the current device.
   * @param version - Version of the application installed.
   * @returns Boolean value indication whether the app. version is valid or user needs to update the application.
   */
  private isAppVersionValid(deviceOS: string, version: number) {
    // If user is not logged-in then we must not check app-version.
    if (!version) {
      return true;
    } else if (this.utilityService.isDeviceAndroid(deviceOS)) {
      return version >= environment.appVersions.android;
    } else {
      return version >= environment.appVersions.ios;
    }
  }

  /**
   * Method that reads user-profile from various sources and formats them.
   * @returns Observable containing the formatted user profile object.
   */
  initUserProfile() {
    const { deviceOS, isWebView } = this.utilityService.getDeviceOS();

    return new Observable<UserProfile>((observer) => {
      /**
       * Method to catch any errors occurred while fetching user profile.
       */
      const catchProfileError = (error: any) => {
        this.matDialog.open(ErrorDialogComponent, { data: 'error.wrong' });
        observer.error(error);
      };

      /**
       * Method that emits a `next` and `complete` notification.
       * @param userProfile - Fetched user profile object.
       */
      const emitUserProfile = (userProfile: UserProfile) => {
        observer.next(userProfile);
        // Observables needs to be complete for the APP_INITIALISER.
        observer.complete();
      };
      if(isPlatformServer(this.platformId)){
        emitUserProfile(this.formatAuthForWeb({}));
        return;
      }

      try {
        // Fetching user profile on web.
        if (!isWebView) {
          const encryptAuthDtls = localStorage.getItem('userInfo')!;
          const decryptedDtls = this.decryptUserProfile(encryptAuthDtls);
          const isLocalEnv = window.location.hostname.includes('localhost');

          // Fetching dummy user profile (on local env.).
          if (!decryptedDtls && isLocalEnv) {
            this.callApiService
              .fetchUserProfile()
              .pipe(take(1))
              .subscribe({
                next: (profileData) => {
                  emitUserProfile(this.formatAuthForWeb(profileData));
                },
                error: catchProfileError,
              });
            return;
          }

          emitUserProfile(this.formatAuthForWeb(decryptedDtls));
        }
      } catch (error) {
        catchProfileError(error);
      }
    }).pipe(
      map((userProfile) => {
        // Checking for a valid native app. version.
        if (isWebView && !this.isAppVersionValid(deviceOS, userProfile.ver)) {
          this.matDialog.open(UpdateAppDialogComponent);
          throw new Error('Invalid application version.');
        }

        return {
          ...userProfile,
          mode:
            deviceOS.toLowerCase() === devicePlatforms.android
              ? 'app'
              : deviceOS,
          trkr: this.generateTRKR(),
        };
      }),
      tap({
        next: (userProfile) => {
          this.cachingService.userProfile = userProfile;
        },
      })
    );
  }
}
